import { FormikProps } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import {
  useGetNotificationVariableListQuery,
  useGetSystemRuleQuery,
  useLazyOrganizationGroupsQuery,
  useLazyOrganizationVehiclesQuery,
  useSingleOrganizationVehicleQuery,
} from "../../../api/network/adminApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { OrgGHGroup, TLegacyGroup } from "../../../api/types/Group";
import {
  LegacyRuleEntityListingPayload,
  TLegacyRule,
} from "../../../api/types/Rule";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import FormActionButtons from "../../../components/admin/formActionButtons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldCheckboxGroup,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldTextarea,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncDebounce } from "../../../utils/common";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import { SYSTEM_CRON_RULE_OPTIONS } from "./constant";
import { ruleDetailsInitialValues } from "./validation";
interface RuleGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  formik?: FormikProps<typeof ruleDetailsInitialValues>;
  updateFormikValues: (values: TLegacyRule | undefined) => void;
}

export const RuleGeneralDetailForm: FC<RuleGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  formik,
  updateFormikValues,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "manageRuleAlert.detailsPage.accord_general_details_form",
  });

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { data: dataSystemRules, isFetching: isFetchingSystemRules } =
    useGetSystemRuleQuery({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
      search: "",
    } as LegacyRuleEntityListingPayload);

  const [systemRule, setSystemRule] = useState("");

  const MemoizedFormActionEmailButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: `w-28 mt-6`,
              buttonDimensionClass: "h-11 w-full px-4",
              label: t("add_email_button"),
              variant: "primary",
              type: "button",
              onClick: () => {
                formikSetValue("notification_email", [
                  ...values.notification_email,
                  values.email,
                ]);
                formikSetValue("email", "");
                setTimeout(() => {
                  formik?.validateForm();
                }, 100); // intentional delay to reset validation error when option is selected
              },
              disabled: !values.email || !!errors.email,
            },
          ]}
        />
      </>
    );
  }, [values.notification_email, values.email, errors.email]);
  const MemoizedFormActionPhoneButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: `w-28 mt-6`,
              buttonDimensionClass: "h-11 w-full px-4",
              label: t("add_phone_button"),
              variant: "primary",
              type: "button",
              onClick: () => {
                formikSetValue("notification_phone", [
                  ...values.notification_phone,
                  values.phone,
                ]);
                formikSetValue("phone", "");
                setTimeout(() => {
                  formik?.validateForm();
                }, 100); // intentional delay to reset validation error when option is selected
              },
              disabled: !values.phone || !!errors.phone,
            },
          ]}
        />
      </>
    );
  }, [values.notification_phone, values.phone, errors.phone]);
  const handleUpdateSystemRule = (value: string) => {
    if (value?.length) {
      updateFormikValues(
        dataSystemRules?.results?.find((rule) => rule.ruleid === value)
      );
    }
    setSystemRule(value);
  };

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("ruleid")}
        type="text"
        id="ruleid"
        name="ruleid"
        value={values.ruleid}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.ruleid}
        error={errors.ruleid}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData || isFetchingSystemRules}
        label={t("system_rule")}
        id="system_rule"
        name="system_rule"
        value={systemRule}
        placeholder="Please select the system rule"
        options={dataSystemRules?.results?.map((rule) => ({
          value: rule?.ruleid,
          label: rule?.description,
        }))}
        onChange={(e: any) => {
          handleUpdateSystemRule(e?.value);
        }}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <div className="col-span-12">
        <AdminFormFieldInput
          label={t("selector")}
          type="text"
          id="selector"
          name="selector"
          value={values.selector}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.selector}
          error={errors.selector}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <AdminFormFieldCheckbox
        customWrapperClass="col-span-6 mt-5"
        label={t("is_active")}
        id="is_active"
        type="checkbox"
        name="is_active"
        checked={values.is_active}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.is_active}
        error={errors.is_active}
        disabled={!userCanEdit}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("cron_rule")}
        id="cron_rule"
        name="cron_rule"
        value={values.cron_rule}
        options={SYSTEM_CRON_RULE_OPTIONS}
        onChange={(e: any) => {
          formikSetValue("cron_rule", e?.value);
        }}
        onBlur={(e: any) => {
          formikSetTouched("cron_rule", true);
          handleBlur(e);
        }}
        touched={touched.cron_rule}
        error={errors.cron_rule}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <div className="col-span-12">
        <AdminFormFieldInput
          label={t("description")}
          type="text"
          id="description"
          name="description"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.description}
          error={errors.description}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-6">
        <AdminFormFieldInput
          label={t("minimum_interval")}
          type="text"
          id="minimum_interval"
          name="minimum_interval"
          value={values.minimum_interval}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.minimum_interval}
          error={errors.minimum_interval}
          disabled={!userCanEdit}
          bottomLabel="in seconds"
          detailsFormField={true}
        />
      </div>

      <AdminFormFieldCheckbox
        customWrapperClass="col-span-6 my-5"
        label={t("interval_reset")}
        id="interval_reset"
        type="checkbox"
        name="interval_reset"
        checked={values.interval_reset}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.interval_reset}
        error={errors.interval_reset}
        disabled={!userCanEdit}
      />

      <div className="col-span-12">
        <AdminFormFieldInput
          label={t("predefined_actions")}
          type="text"
          id="predefined_actions"
          name="predefined_actions"
          placeholder="Please provide an actions"
          value={values.predefined_actions}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.predefined_actions}
          error={errors.predefined_actions}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-x-4 mt-1">
        <label
          className={`col-span-12 block text-sm font-display font-semibold text-field-label-valid mb-2`}
        >
          {t("trigger_actions")}
        </label>

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-4"
          label={t("is_email_sms")}
          id="is_email_sms"
          type="checkbox"
          name="is_email_sms"
          checked={values.is_email_sms}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.is_email_sms}
          error={errors.is_email_sms}
          disabled={!userCanEdit}
        />

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-4"
          label={t("is_save_alert")}
          id="is_save_alert"
          type="checkbox"
          name="is_save_alert"
          checked={values.is_save_alert}
          onChange={(e) => {
            handleChange(e);
            formikSetValue("is_queue", false);
            formikSetValue("via_alerts", true);
          }}
          onBlur={handleBlur}
          touched={touched.is_save_alert}
          error={errors.is_save_alert}
          disabled={!userCanEdit}
        />

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-4"
          label={t("is_queue")}
          id="is_queue"
          type="checkbox"
          name="is_queue"
          checked={values.is_queue}
          onChange={(e) => {
            handleChange(e);
            formikSetValue("via_notifications", true);
          }}
          onBlur={handleBlur}
          touched={touched.is_queue}
          error={errors.is_queue}
          disabled={!userCanEdit}
        />
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-x-4 mt-1">
        <label
          className={`col-span-12 block text-sm font-display font-semibold text-field-label-valid mb-2`}
        >
          {t("notify_via")}
        </label>

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-6"
          label={t("notifications")}
          id="via_notifications"
          type="checkbox"
          name="via_notifications"
          checked={values.via_notifications}
          onChange={(e) => {
            handleChange(e);
          }}
          onBlur={handleBlur}
          touched={touched.via_notifications}
          error={errors.via_notifications}
          disabled={!!values.is_queue || !userCanEdit}
          readOnly={!!values.is_queue}
        />

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-6"
          label={t("alerts")}
          id="via_alerts"
          type="checkbox"
          name="via_alerts"
          checked={values.via_alerts}
          onChange={(e) => {
            handleChange(e);
          }}
          onBlur={handleBlur}
          touched={touched.via_alerts}
          error={errors.via_alerts}
          disabled={!!values.is_save_alert || !userCanEdit}
          readOnly={!!values.is_save_alert}
        />
      </div>

      <div className="col-span-6 flex flex-col gap-6">
        {/* Input field with add button */}
        <div className="col-span-12 flex flex-row gap-3">
          <AdminFormFieldInput
            label={t("add_email_title")}
            type="text"
            id="email"
            name="email"
            placeholder={t("add_email_input_placeholder")}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            error={errors.email}
            touched={touched.email}
            customWrapperClass="flex-grow"
            disabled={!userCanEdit}
          />
          {/* <AdminFormFieldSubmit
            customWrapperClass={`w-28 mt-6`}
            buttonDimensionClass="h-11 w-full px-4"
            label={t("add_email_button")}
            variant="primary"
            type="button"
            onClick={() => {
              formikSetValue("notification_email", [
                ...values.notification_email,
                values.email,
              ]);
              formikSetValue("email", "");
              setTimeout(() => {
                formik?.validateForm();
              }, 100); // intentional delay to reset validation error when option is selected
            }}
            disabled={!values.email || !!errors.email}
          /> */}
          {MemoizedFormActionEmailButton}
        </div>

        {/* Pill List */}
        <div className="col-span-12">
          <label
            className={`col-span-12 block text-sm font-display font-semibold mb-1 ${userCanEdit && errors.notification_email ? "text-field-error-dark" : "text-field-label-valid"}`}
          >
            {t("notification_email")}
          </label>

          <AdminFormFieldPillList
            isError={userCanEdit && errors.notification_email}
            disabled={!userCanEdit}
          >
            {values.notification_email.map((email: string, i: number) => (
              <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                key={`pillItem_${i}`}
                item={{ name: email } as PillItem}
                onRemove={() => {
                  formikSetValue(
                    "notification_email",
                    values.notification_email.filter(
                      (item: string) => item !== email
                    )
                  );
                }}
                disabled={!userCanEdit}
              />
            ))}
          </AdminFormFieldPillList>

          {errors.notification_email && userCanEdit && (
            <span className="bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1 col-span-12">
              {errors.notification_email}
            </span>
          )}
        </div>
      </div>

      <div className="col-span-6 flex flex-col gap-6">
        {/* Input field with add button */}
        <div className="col-span-12 flex flex-row gap-3">
          <AdminFormFieldInput
            label={t("add_phone_title")}
            type="text"
            id="phone"
            name="phone"
            placeholder={t("add_phone_input_placeholder")}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.phone}
            error={errors.phone}
            touched={touched.phone}
            customWrapperClass="flex-grow"
            disabled={!userCanEdit}
          />
          {/* <AdminFormFieldSubmit
            customWrapperClass={`w-28 mt-6`}
            buttonDimensionClass="h-11 w-full px-4"
            label={t("add_phone_button")}
            variant="primary"
            type="button"
            onClick={() => {
              formikSetValue("notification_phone", [
                ...values.notification_phone,
                values.phone,
              ]);
              formikSetValue("phone", "");
              setTimeout(() => {
                formik?.validateForm();
              }, 100); // intentional delay to reset validation error when option is selected
            }}
            disabled={!values.phone || !!errors.phone}
          /> */}
          {MemoizedFormActionPhoneButton}
        </div>

        {/* Pill List */}
        <div className="col-span-12">
          <label
            className={`col-span-12 block text-sm font-display font-semibold mb-1 ${userCanEdit && errors.notification_phone ? "text-field-error-dark" : "text-field-label-valid"}`}
          >
            {t("notification_phone")}
          </label>

          <AdminFormFieldPillList
            isError={userCanEdit && errors.notification_phone}
            disabled={!userCanEdit}
          >
            {values.notification_phone.map((phone: string, i: number) => (
              <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                key={`pillItem_${i}`}
                item={{ name: phone } as PillItem}
                onRemove={() => {
                  formikSetValue(
                    "notification_phone",
                    values.notification_phone.filter(
                      (item: string) => item !== phone
                    )
                  );
                }}
                disabled={!userCanEdit}
              />
            ))}
          </AdminFormFieldPillList>
          {errors.notification_phone && userCanEdit && (
            <span className="bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1 col-span-12">
              {errors.notification_phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

interface RuleFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formik?: FormikProps<typeof ruleDetailsInitialValues>;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
}

export const RuleAdvanceDetailForm: FC<RuleFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  formik,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "manageRuleAlert.detailsPage.accord_advance_details_form",
  });
  // Step 1: Set up state for selected values
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Define custom options for the checkbox group
  const options = [
    { label: "Subject", value: "subject" },
    { label: "SMS", value: "sms" },
    { label: "Email", value: "email" },
  ];

  // Step 2: Handle changes to selected values
  const handleCheckboxChange = (values: string[]) => {
    setSelectedValues(values);
  };

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const {
    data: dataNotificationVariable,
    isFetching: isFetchingNotificationVariable,
  } = useGetNotificationVariableListQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
    search: "",
  } as LegacyRuleEntityListingPayload);

  const [notificationVariableValue, setNotificationVariableValue] =
    useState("");

  const MemoizedFormActionApplyButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "button",
              variant: "primary-like",
              label: (
                <span className="flex justify-center whitespace-nowrap">
                  Apply
                </span>
              ),
              onClick: () => onSubmit(),
              disabled:
                !selectedValues.length || !notificationVariableValue.length,
            },
          ]}
        />
      </>
    );
  }, [selectedValues.length, notificationVariableValue.length]);
  const onSubmit = () => {
    if (selectedValues?.includes("subject")) {
      formikSetValue(
        "email_subject",
        values.email_subject + " " + notificationVariableValue
      );
    }

    if (selectedValues?.includes("sms")) {
      formikSetValue(
        "sms_message",
        values.sms_message + " " + notificationVariableValue
      );
    }

    if (selectedValues?.includes("email")) {
      formikSetValue(
        "email_message",
        values.email_message + " " + notificationVariableValue
      );
    }

    setTimeout(() => {
      formik?.validateForm();
    }, 100); // intentional delay to reset validation error when option is selected
  };

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDropdown
        loadingData={loadingData || isFetchingNotificationVariable}
        label={t("notification_variables")}
        id="notification_variables"
        name="notification_variables"
        placeholder="Please select variables"
        value={notificationVariableValue}
        options={dataNotificationVariable?.results?.map((noti) => ({
          value: noti?.variable,
          label: noti?.name,
        }))}
        onChange={(e: any) => {
          setNotificationVariableValue(e?.value);
        }}
        disabled={!userCanEdit}
        customWrapperClass="col-span-4"
        detailsFormField={true}
      />

      <AdminFormFieldCheckboxGroup
        options={options}
        layout="row" // or "row"
        values={selectedValues}
        disabled={!userCanEdit}
        onChange={handleCheckboxChange} // Step 3: Pass the change handler
        customWrapperClass="col-span-6 mt-5"
      />

      <div className="col-span-2 mt-6">
        {/* <AdminFormFieldSubmit
          type="button"
          variant="primary-like"
          label={
            <span className="flex justify-center whitespace-nowrap">Apply</span>
          }
          onClick={onSubmit}
          disabled={!selectedValues.length || !notificationVariableValue.length}
        /> */}
        {MemoizedFormActionApplyButton}
      </div>

      <div className="col-span-12">
        <AdminFormFieldInput
          label={t("email_subject")}
          type="text"
          id="email_subject"
          name="email_subject"
          value={values.email_subject}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.email_subject}
          error={errors.email_subject}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12">
        <AdminFormFieldTextarea
          label={t("email_message")}
          id="email_message"
          name="email_message"
          placeholder="Enter your description here..."
          value={values.email_message}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.email_message}
          error={errors.email_message}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12">
        <AdminFormFieldTextarea
          label={t("sms_message")}
          id="sms_message"
          name="sms_message"
          placeholder="Enter your description here..."
          value={values.sms_message}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.sms_message}
          error={errors.sms_message}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
    </div>
  );
};

export const RuleStatusCodeDetailForm: FC<RuleFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix:
      "manageRuleAlert.detailsPage.accord_status_code_selection_details_form",
  });
  const { t: tMain } = useTranslation("translation");

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  // Step 1: Set up state for selected values
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Define custom options for the checkbox group
  const options = [
    { label: "Assets", value: "assets" },
    { label: "Groups", value: "groups" },
  ];

  // Step 2: Handle changes to selected values
  const handleCheckboxChange = (values: string[]) => {
    // If the selected value is already in selectedValues, clear the selection
    if (selectedValues.length > 0 && selectedValues[0] === values[0]) {
      setSelectedValues([]); // Deselect
    } else {
      setSelectedValues(values); // Select the new value
      if (values[0] === "assets") {
        formikSetValue("isassets", true);
        formikSetValue("isgroups", false);
      } else {
        formikSetValue("isassets", false);
        formikSetValue("isgroups", true);
      }
    }
  };

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  const [orgGroupsQuery, { isLoading: loadingGroupData }] =
    useLazyOrganizationGroupsQuery();
  const [dropdownGroupsSearchResults, setDropdownGroupsSearchResults] =
    useState<TLegacyGroup[]>([]);

  const loadOptionsHandlerGroup = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: groupsQueryResponse } = await orgGroupsQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2, // start from page 2 as page 1 is already loaded from addition prop of component
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_DROPDOWN.join(","), // search by groupid only
    });

    if (!groupsQueryResponse || !groupsQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = groupsQueryResponse as { results: TLegacyGroup[] };
    setDropdownGroupsSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyGroup[])).map(
            (item: TLegacyGroup, index: number) =>
              ({
                value: `${item.groupid}`,
                label: item.groupid,
              }) as TSelectboxOption
          )
        : [],
      hasMore: groupsQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerGroup = asyncDebounce(
    loadOptionsHandlerGroup,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const [
    orgVehiclesQuery,
    { isLoading: loadingvehicleData, isFetching: isFetchingVehicleData },
  ] = useLazyOrganizationVehiclesQuery();
  const [dropdownVehicleSearchResults, setDropdownVehicleSearchResults] =
    useState<TLegacyVehicle[]>([]);

  const MemoizedFormActionSelectGroupsButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: tMain("select_groups_button"),
              type: "button",
              variant: "primary-like",
              customWrapperClass: "col-span-3 ml-2",
              buttonDimensionClass: "h-11 w-full px-4",
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showHierarchyGroupSelector: true,
                  })
                );
              },
              disabled:
                !userCanEdit || !values?.isgroups || values.groups === "*", // disable if user cannot edit or all groups is selected
            },
          ]}
        />
      </>
    );
  }, [userCanEdit, values?.isgroups, values.groups, modalsState]);

  const { data: singleVehicleData, isFetching: isFetchingSingleVehicle } =
    useSingleOrganizationVehicleQuery(
      {
        account_id: thisUserOrganizationId,
        device_id: values.assets,
      },
      {
        skip: !values.assets || values.assets === "*" || values.assets === "-",
      }
    );
  const loadOptionsHandlerVehicle = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: groupsVehicleResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2, // start from page 2 as page 1 is already loaded from addition prop of component
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    });

    if (!groupsVehicleResponse || !groupsVehicleResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = groupsVehicleResponse as {
      results: TLegacyVehicle[];
    };
    setDropdownVehicleSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyVehicle[])).map(
            (item: TLegacyVehicle, index: number) =>
              ({
                value: `${item.deviceid}`,
                // label: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
                label: consistentDisplayAssetItem(
                  item.deviceid,
                  item.description,
                  item.displayname,
                  item.drivername,
                  showDriver
                ),
              }) as TSelectboxOption
          )
        : [],
      hasMore: groupsVehicleResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerVehicle = asyncDebounce(
    loadOptionsHandlerVehicle,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  useEffect(() => {
    if (values?.isassets) {
      setSelectedValues(["assets"]);
    } else {
      if (values?.isgroups) {
        setSelectedValues(["groups"]);
      }
    }

    if (!values?.isassets && !values?.isgroups) {
      setSelectedValues([]);
    }
  }, [values]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <label
        className={`col-span-12 block text-sm font-display font-semibold text-field-label-valid`}
      >
        {t("assign_to")}
      </label>
      <AdminFormFieldCheckboxGroup
        options={options}
        layout="row" // or "row"
        values={selectedValues}
        disabled={!userCanEdit}
        onChange={handleCheckboxChange} // Step 3: Pass the change handler
        customWrapperClass="col-span-5"
        selectionMode="single"
      />

      <div className="col-span-12 grid grid-cols-12 gap-x-4 mt-1">
        <AdminFormFieldAsyncLazyDropdown
          loadingData={
            loadingData ||
            loadingvehicleData ||
            loadingvehicleData ||
            isFetchingSingleVehicle ||
            isFetchingVehicleData
          }
          label={t("assets")}
          id="assets"
          name="assets"
          placeholder="No Groups (Select by vehicle)"
          value={
            !!values?.assets
              ? values.assets === "-"
                ? ""
                : values.assets
              : undefined
          }
          defaultOptions={[{ value: "*", label: "All" }]}
          defaultOption={
            !!values?.assets
              ? values.assets === "-"
                ? undefined
                : {
                    value: values.assets,
                    label: singleVehicleData
                      ? consistentDisplayAssetItem(
                          singleVehicleData.deviceid,
                          singleVehicleData.description,
                          singleVehicleData.displayname,
                          singleVehicleData.drivername,
                          showDriver
                        )
                      : values.assets,
                  }
              : undefined
          }
          loadOptions={debouncedLoadOptionsHandlerVehicle}
          onChange={(e: any) => {
            formikSetValue("assets", e?.value);
          }}
          onBlur={(e: any) => {
            formikSetTouched("assets", true);
            handleBlur(e);
          }}
          additional={{ page: 1 }}
          touched={touched.assets}
          error={errors.assets}
          disabled={!userCanEdit || !values?.isassets}
          detailsFormField={true}
        />

        <div className="col-span-6 grid grid-cols-6">
          {loadingData ? (
            <PseudoSelect label={t("select_group_to_add")} />
          ) : (
            <>
              {accountHasGroupHierarchyEnabled === "1" ? (
                <>
                  <label
                    htmlFor="assets"
                    className="col-span-6 block text-sm font-display font-semibold text-field-label-valid"
                  >
                    {t("groups")}
                  </label>
                  <AdminFormFieldCheckbox
                    label="All Groups"
                    id="all_groups"
                    type="checkbox"
                    name="all_groups"
                    checked={values.groups === "*"}
                    // onChange={(e) => { handleChange?.(e); addAllGroups(); setTimeout(() => { formikSetTouched('selected_groups'); }, 100); }}
                    onChange={(e) => {
                      formikSetValue("groups", e.target.checked ? "*" : null);
                    }}
                    onBlur={handleBlur}
                    error={errors.all_groups}
                    touched={touched.all_groups}
                    disabled={!userCanEdit}
                    customWrapperClass="col-span-3 mr-2"
                    wrapperPaddingClass="px-3 py-2"
                  />
                  {/* <AdminFormFieldSubmit
                      label={tMain('select_groups_button')}
                      type="button"
                      variant="primary-like"
                      customWrapperClass="col-span-3 ml-2"
                      buttonDimensionClass="h-11 w-full px-4"
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showHierarchyGroupSelector: true, })) }}
                      disabled={!userCanEdit || !values?.isgroups || values.groups === '*'} // disable if user cannot edit or all groups is selected
                    /> */}
                  {MemoizedFormActionSelectGroupsButton}
                  <div className="col-span-6 mt-2">
                    {modalsState.showHierarchyGroupSelector && (
                      <HierarchyGroupSelector
                        initiallySelectedGroups={
                          values.groups
                            ? [values.groups].filter(
                                (group: string) => !!group && group !== "-"
                              )
                            : []
                        }
                        onSelectionChange={(selectedGroups: string[]) => {
                          if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                            console.log("VALUES RECEIVED:", selectedGroups);
                          formikSetValue("groups", selectedGroups?.[0] || null);
                          setTimeout(() => {
                            formikSetTouched("groups");
                          }, 100); // to trigger validation
                        }}
                        selectLimit={1}
                      />
                    )}
                  </div>
                  <AdminFormFieldPillList
                    customWrapperClass="col-span-6 mt-1"
                    disabled={
                      !userCanEdit || !values?.isgroups || values.groups === "*"
                    }
                    isError={!!errors.groups && !!userCanEdit}
                  >
                    {(values.groups === "*" ? [] : [values.groups])
                      .filter((group: string) => !!group && group !== "-")
                      .map((group: string, i: number) => (
                        <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                          key={`pillItem_${i}`}
                          disabled={
                            !userCanEdit ||
                            !values?.isgroups ||
                            values.groups === "*"
                          }
                          item={{ name: group } as PillItem}
                          onRemove={() => {
                            formikSetValue("groups", null);
                            formikSetTouched("groups", true);
                          }}
                          onItemClick={
                            accountHasGroupHierarchyEnabled === "1"
                              ? (item) => {
                                  dispatch(
                                    setModalsData({
                                      ...modalsState,
                                      showReadOnlyViewForHierarchyGroup: {
                                        accountid: thisUserOrganizationId,
                                        active_inactive_vehicle_counts: {
                                          active_vehicle_count: 0,
                                          inactive_vehicle_count: 0,
                                        },
                                        description: "",
                                        groupid: group,
                                      } as OrgGHGroup,
                                      showHierarchyGroupSelector: true,
                                    })
                                  );
                                }
                              : undefined
                          }
                        />
                      ))}
                  </AdminFormFieldPillList>
                </>
              ) : (
                <AdminFormFieldAsyncLazyDropdown
                  loadingData={loadingData || loadingGroupData}
                  label={t("groups")}
                  id="groups"
                  name="groups"
                  placeholder="No Groups (Select by asset)"
                  value={
                    !!values?.groups
                      ? values.groups === "-"
                        ? ""
                        : values.groups
                      : undefined
                  }
                  defaultOptions={[
                    { value: "*", label: "All" }, // Adding the "All" option
                  ]}
                  defaultOption={
                    !!values?.groups
                      ? values.groups === "-"
                        ? undefined
                        : { value: values.groups, label: values.groups }
                      : undefined
                  }
                  loadOptions={debouncedLoadOptionsHandlerGroup}
                  onChange={(e: any) => {
                    formikSetValue("groups", e?.value);
                  }}
                  onBlur={(e: any) => {
                    formikSetTouched("groups", true);
                    handleBlur(e);
                  }}
                  additional={{ page: 1 }}
                  touched={touched.groups}
                  error={errors.groups}
                  disabled={!userCanEdit || !values?.isgroups}
                  detailsFormField={true}
                  menuPlacement="top"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
